import { flash, Modal, roundNumber, SubmitButton } from "@xola/ui-kit";
import {
    FormField,
    getRefundLineItems,
    handleSuccessfulSubmission,
    InputField,
    isCreditCardPayment,
    isEquipmentItem,
    isMembershipItem,
    isMistPurchase,
    isOtherSellerPurchase,
    isSettledByCancellationProtection,
    isStoreCreditPurchaseItem,
    isStripeEnabled,
    requiredNumberMoreThanZero,
    requiredString,
} from "common";
import { Field } from "formik";
import { isEmpty, isNil, isUndefined, omit } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    Event,
    LineItemType,
    PaymentMethod,
    Product,
    Purchase,
    PurchaseItem,
    RemoteCard,
    SellerPaymentMethod,
} from "types";
import { boolean, object, string } from "yup";
import { api } from "../../../api/api";
import { almostEquals } from "../../../helpers/mathHelper";
import { useResource } from "../../../hooks/useResource";
import { useVisibility } from "../../../hooks/useVisibility";
import { getExcludedPaymentMethods, getOnlinePaymentMethodsFromPurchase } from "../../../models/Payment";
import {
    getAmountPaidByCard,
    getInteracPaymentLineItem,
    getPartnerFeePaidByTraveler,
    getPurchaseProductId,
    isExperiencePurchaseItem,
    isMultiplePaymentMethods,
    isPackage,
    isPurchaseWithInteracCard,
    shouldEnableDrawerEject,
} from "../../../models/Purchase";
import { getAutoPrint } from "../../../storages/PrinterStorage";
import { updateCache } from "../../../utils/updateCache";
import { Currency } from "../../Common/Currency";
import { NotifyCustomerCheckbox } from "../../Common/NotifyCustomerCheckbox";
import { Printer } from "../../Common/Printer";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { RefundOption } from "../../Dashboard/RefundOption";
import { RefundOptionsDropdown } from "../../Dashboard/RefundOptionsDropdown";
import { CurrencyInputField } from "../../Field/CurrencyInputField";
import { Loader } from "../../Loader";
import { PaymentBreakdown } from "../PaymentBreakdown";
import { PaymentMethodButtons } from "../PaymentMethodButtons";
import { PurchaseItemSelectWrapper } from "../PurchaseItemSelect";
import { PurchaseSummaryTile } from "../PurchaseSummaryTile";
import { InteracPaymentMessage, SavedCards } from "../SavedCardMenu";
import { TransferToStoreCreditModal } from "../TransferToStoreCreditModal";
import { CancellationProtectionRefundBlocked } from "./CancellationProtectionRefundBlocked";
import { getPayload, getPurchasePaymentMethodForRefund, getRefundAmount } from "./helpers";
import { RefundToStoreCreditWarning } from "./RefundToStoreCreditWarning";
import { RefundWarnings } from "./RefundWarnings";
import { queryClient } from "@/api/queryClient";
import { updatePurchaseTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { getPairedDevice } from "@/components/Input/EmvInput";
import { EmvRefundInput } from "@/components/Input/EmvRefundInput";
import { PaymentInputRef } from "@/components/Input/PaymentInputRef";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { invalidateSellerBalance } from "@/models/Seller";

const restrictedPaymentMethods = new Set<string>([PaymentMethod.GIFT, PaymentMethod.VOUCHER]);

const getValidationSchema = (
    purchase: Purchase,
    item: PurchaseItem | null,
    paymentMethod: SellerPaymentMethod | null,
    isInteracCardRefundFlow?: boolean,
) => {
    const maxAmount = getRefundAmount(purchase, item, paymentMethod);

    return object().shape({
        amount: requiredNumberMoreThanZero().max(maxAmount, `cannot exceed paid amount ${maxAmount}`),
        comment: isInteracCardRefundFlow ? string().notRequired() : requiredString(),
        notify: boolean(),
        paymentMethod: object().shape({
            id: requiredString().test(
                "cantBeGiftOrVoucher",
                "Payment Method is required",
                (value) => !value || !restrictedPaymentMethods.has(value),
            ),
        }),
    });
};

export enum ReturnPaymentSource {
    DEFAULT = "default",
    CHANGE_GUEST_QUANTITY = "change-guest-quantity",
    CHANGE_MERCHANDISE_QUANTITY = "change-merchandise-quantity",
    CHANGE_ADD_ONS = "change-add-ons",
    CHANGE_ARRIVAL = "change-arrival",
    SWITCH_EXPERIENCE = "switch-experience",
    SPLIT_PACKAGE = "split-package",
}

interface PurchaseReturnPaymentModalProps {
    isOpen: boolean;
    purchase: Purchase;
    item?: PurchaseItem;
    event?: Event;
    /**
     * This is used when you want to reduce AND return. Setting this to false is just returning money
     * and there maybe a balance left on the booking
     */
    shouldReduce: boolean;
    onClose: () => void;
    manualRefund?: (amountRefunded: number) => void;
    refundAmount: number | null;
    onContinue?: () => void;
    refundOption?: RefundOption | null;
    onChangeRefundOption?: (option: RefundOption) => void;
    onError?: (purchase?: Purchase) => void;
    /**
     * @property {boolean=false} returnAll - if true, in case of package or mist purchase, after returning funds of by
     * one listing it will suggest to return by rest listings in current purchase.
     * */
    returnAll?: boolean;

    /**
     * When modal is opened for {@link RefundOption.RETURN_PAYMENT},
     * we need to know if it is open directly from user interaction or automatically after user changes guest quantity
     */
    returnPaymentSource?: ReturnPaymentSource | null;
    isCancelAction?: boolean;
}

export const getNextReturnPaymentItem = (
    purchase: Purchase,
    selectedItemId?: ID,
    isCancelAction = false,
): PurchaseItem | undefined => {
    return purchase.items.find((item) => {
        if (item.id === selectedItemId) {
            return false;
        }

        if (isCancelAction && isMembershipItem(item)) {
            return false;
        }

        return item.amount !== 0;
    });
};

export const PurchaseReturnPaymentModal = ({
    isOpen,
    purchase,
    item,
    event,
    shouldReduce = true,
    refundAmount,
    returnAll = false,
    refundOption,
    manualRefund,
    onClose,
    onContinue,
    onChangeRefundOption,
    returnPaymentSource = ReturnPaymentSource.DEFAULT,
    isCancelAction = false,
    onError,
}: PurchaseReturnPaymentModalProps) => {
    const { isGranted } = useAuthorization();

    const handleOnClose = () => {
        setTimeout(() => {
            updatePurchaseTimeslotCache(purchase, isGranted);
        }, 1000); // give some time for elastic search to get updated and invalidate to get new data

        onClose();
    };

    if (isSettledByCancellationProtection(purchase, item)) {
        return (
            <Modal size="huge" isOpen={isOpen} onClose={handleOnClose}>
                <CancellationProtectionRefundBlocked onClose={handleOnClose} />
            </Modal>
        );
    }

    return (
        <Modal size="huge" isOpen={isOpen} onClose={handleOnClose}>
            <ModalContent
                purchase={purchase}
                item={item}
                event={event}
                shouldReduce={shouldReduce}
                refundAmount={refundAmount}
                manualRefund={manualRefund}
                returnAll={returnAll}
                refundOption={refundOption}
                returnPaymentSource={returnPaymentSource}
                isCancelAction={isCancelAction}
                onClose={handleOnClose}
                onContinue={onContinue}
                onChangeRefundOption={onChangeRefundOption}
                onError={onError}
            />
        </Modal>
    );
};

const ModalContent = ({
    purchase,
    item,
    event,
    shouldReduce,
    manualRefund,
    refundAmount,
    returnAll,
    refundOption,
    onClose,
    onContinue,
    onChangeRefundOption,
    returnPaymentSource,
    isCancelAction,
    onError,
}: Except<PurchaseReturnPaymentModalProps, "isOpen">) => {
    const [selectedItem, setSelectedItem] = useState(item ?? (shouldReduce ? purchase.items[0] : null));
    const previousItemIdRef = useRef<PurchaseItem["id"] | undefined>(item?.id);
    const itemId = item?.id;
    const hasItemPropertyChanged = previousItemIdRef.current !== itemId;
    const seller = useSeller();

    useEffect(() => {
        if (!hasItemPropertyChanged) {
            return;
        }

        previousItemIdRef.current = itemId;

        if (item) {
            setSelectedItem(item);
        }
    }, [hasItemPropertyChanged, item, itemId]);

    const shouldSetDefaultItem = !item && shouldReduce && !selectedItem;
    const defaultItem = purchase.items[0] ?? null;

    useEffect(() => {
        if (shouldSetDefaultItem && defaultItem) {
            setSelectedItem(defaultItem);
        }
    }, [shouldSetDefaultItem, defaultItem]);

    // Exclude items that no longer have any value — there's nothing to reduce, so they shouldn't appear in
    // the selector or the cancel-all "Next" sequence (matches getNextReturnPaymentItem); the excess is
    // returned afterwards via "Refund Excess Payment".
    const allowedItemsToReturnPayment = isCancelAction
        ? purchase.items.filter((item) => !isMembershipItem(item) && item.amount !== 0)
        : purchase.items;

    // Purchase item data can be updated in the background, so we need to use the most recent data for the payload and summary
    useEffect(() => {
        const selectedItemFromPurchase = purchase.items.find((item) => item.id === selectedItem?.id);
        if (selectedItemFromPurchase) {
            handleSelectedItem(selectedItemFromPurchase);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchase]);

    useEffect(() => {
        /** For cancellation action, we don't want to show membership items in product selector */
        if (!isCancelAction || !selectedItem || !isMistPurchase(purchase)) {
            return;
        }

        if (
            allowedItemsToReturnPayment.length > 0 &&
            !allowedItemsToReturnPayment.some((item) => item.id === selectedItem.id)
        ) {
            setSelectedItem(allowedItemsToReturnPayment[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCancelAction, allowedItemsToReturnPayment]);

    const productId = getPurchaseProductId(purchase, selectedItem?.id);
    const { data: product, isLoading } = useResource<Product>(["products", productId], null, { enabled: !!productId });
    const isExperienceItem = isExperiencePurchaseItem(selectedItem) && !isPackage(product);

    // TODO: We should always get a payment method from a purchase for refunds, otherwise a refund is not needed
    //       Change the type below to remove null
    const initialPaymentMethod = getPurchasePaymentMethodForRefund(purchase);
    const [paymentMethod, setPaymentMethod] = useState<SellerPaymentMethod | null>(initialPaymentMethod);

    const [showTransferForm, { show: openTransferForm, hide: hideTransferForm }] = useVisibility(false);
    const [serviceFeeDelta, setServiceFeeDelta] = useState(0); // This needs to be tracked for flex level because service fee changes and we need to change the refund amount
    const [selectedCard, setSelectedCard] = useState<RemoteCard | null>();
    const [isUpdatingPurchase, setIsUpdatingPurchase] = useState(false);
    const [isPaymentMethodChanging, setIsPaymentMethodChanging] = useState(false);
    const isCCPaymentMethod = paymentMethod?.id === PaymentMethod.CREDIT_CARD;

    const isInteracCard = selectedCard && isPurchaseWithInteracCard(purchase, selectedCard);
    const hasDevice = !!getPairedDevice(seller) || window.flags?.stripe?.simulateTerminal;
    const isInteracCardRefundFlow = isCCPaymentMethod && !!isInteracCard && isStripeEnabled(seller);
    const emvRef = useRef<PaymentInputRef>(null);

    const isStoreCredit = isStoreCreditPurchaseItem(item);
    const [enableDrawerEject, setEnableDrawerEject] = useState<boolean>(false);

    const headerText = shouldReduce && !isStoreCredit ? RefundOption.REDUCE_AND_RETURN : RefundOption.RETURN_PAYMENT;

    const initialComment =
        returnAll || isCancelAction
            ? "Cancellation"
            : headerText === RefundOption.RETURN_PAYMENT &&
              returnPaymentSource === ReturnPaymentSource.CHANGE_GUEST_QUANTITY
            ? isEquipmentItem(selectedItem)
                ? "Change Equipments"
                : "Change Guests"
            : headerText === RefundOption.RETURN_PAYMENT &&
              returnPaymentSource === ReturnPaymentSource.CHANGE_MERCHANDISE_QUANTITY
            ? "Change Merchandise Quantity"
            : "";

    const [commentState, setCommentState] = useState<{ comment: string; touched: boolean }>({
        comment: "",
        touched: false,
    });

    const [isNotifyCustomer, setIsNotifyCustomer] = useState(true);

    const {
        mutateAsync: returnPayment,
        isLoading: isUpdating,
        isSuccess,
    } = useMutation(async (payload: ReturnType<typeof getPayload>) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
        return data;
    });

    useEffect(() => {
        const isSameCard = paymentMethod?.card === selectedCard?.id;
        if (selectedCard?.id && isCreditCardPayment(paymentMethod)) {
            if (!isSameCard || !paymentMethod?.card) {
                setPaymentMethod((paymentMethod) =>
                    paymentMethod ? { ...paymentMethod, card: selectedCard.id } : null,
                );
            }
        }
        // In case of Automatic card selection, we want to clear the card
        else if (isNil(selectedCard?.id) && paymentMethod?.card) {
            setPaymentMethod((paymentMethod) => {
                if (paymentMethod) {
                    return omit(paymentMethod, "card");
                }

                return null;
            });
        }
    }, [selectedCard, paymentMethod]);

    const handleSelectedItem = (item: PurchaseItem) => {
        setSelectedItem(item);
        setCommentState({ comment: "", touched: false });
        setAmountState({ enteredAmount: undefined, touched: false });
    };

    const handleSubmitCommon = async (payload: any, amountToRefund: number) => {
        let updatedPurchase: Purchase;
        let amountToRefundWithServiceFee = amountToRefund;

        if (isInteracCardRefundFlow) {
            if (shouldReduce) {
                const refundLineItem = payload.ops.pop(); // Remove the refund line item from ops, as EMV will handle it separately
                amountToRefundWithServiceFee = refundLineItem.data?.amount;
                updatedPurchase = await returnPayment(payload);
            }

            const params = {
                // we cannot refund more than paid by the interac card value
                amountDue: Math.min(amountToRefundWithServiceFee, selectedCard?.value ?? amountToRefund),
                last4: selectedCard?.last4,
                clientSecret: selectedCard
                    ? getInteracPaymentLineItem(purchase, selectedCard)?.payment?.remoteId
                    : undefined,
            };
            try {
                await emvRef.current?.confirm?.(params);
            } catch {}

            updatedPurchase = queryClient.getQueryData<Purchase>(["purchases", purchase.id]) ?? purchase;
        } else {
            try {
                updatedPurchase = await returnPayment(payload);
            } catch {
                if (shouldReduce) {
                    const { data: newPurchase } = await api.get<Purchase>(`/purchases/${purchase.id}`);
                    updateCache("purchases", newPurchase);
                    onError?.(newPurchase);
                }

                return;
            }
        }

        const lineItems = updatedPurchase.lineItems.reverse(); // Reverse to get the latest line item first
        const amountRefunded = lineItems.find((item) => item.type === LineItemType.REFUND)?.amount ?? 0;
        const isStoreCreditPaymentMethod = paymentMethod?.id === PaymentMethod.STORE_CREDIT;
        const showManualRefund = !(updatedPurchase.balance === 0 && isStoreCredit);
        const refundLineItems = getRefundLineItems(purchase);
        const hasStoreCreditRefundItems = refundLineItems.some((item) => item.refundToStoreCredit);
        const nextItem = getNextReturnPaymentItem(updatedPurchase, selectedItem?.id, isCancelAction);
        const isCardRefund = selectedCard && getAmountPaidByCard(purchase, selectedCard) === amountRefunded;

        try {
            if (returnAll && nextItem) {
                handleSelectedItem(nextItem);
                updateCache("purchases", updatedPurchase);
                handlePaymentBreakdownUpdate(updatedPurchase, nextItem.id);
            } else {
                // Added more timeout here as the modal closes too soon here to see the success animation
                handleSuccessfulSubmission(() => {
                    onContinue?.();
                    if (
                        // Manual refund modal shown only when the refund amount doesn't match the difference between
                        // new and previous purchase balance
                        updatedPurchase.amount > updatedPurchase.balance &&
                        !almostEquals(amountToRefund + serviceFeeDelta, amountRefunded) &&
                        // Case where we want to refund but there is existing refund done to store credit, in that case we won't be refunding entire amount to be refunded.
                        !(isStoreCreditPaymentMethod || hasStoreCreditRefundItems) &&
                        !isCardRefund &&
                        !isInteracCardRefundFlow &&
                        showManualRefund
                    ) {
                        manualRefund?.(amountToRefund);
                    }

                    if (getAutoPrint()) {
                        const drawerEjectPreference = shouldEnableDrawerEject(updatedPurchase);
                        setEnableDrawerEject(drawerEjectPreference);
                    }

                    if (!event) {
                        updateCache("purchases", updatedPurchase);
                    }

                    if (paymentMethod?.card) {
                        invalidateSellerBalance();
                    }

                    onClose();
                });
            }
        } catch {
            flash.danger("Error returning payment");
        }
    };

    const handleSubmit = async (values: any) => {
        /**
         * When we are returning payment, we need to check the payment made
         * on the whole purchase and not just the selected item.
         *
         * The payment method matters: store credit cannot take the service fee, so without it
         * the ceiling comes back higher than the user can enter and a full refund reads as partial.
         */
        const maxAmount = getRefundAmount(purchase, null, paymentMethod);
        const refundLineItems = getRefundLineItems(purchase);
        const isFullRefund = almostEquals(values.amount, maxAmount) && isEmpty(refundLineItems);
        const isStoreCreditPayment = paymentMethod?.id === PaymentMethod.STORE_CREDIT;

        // We will open Transfer to Store Credit modal for partial refund to store credit when there are multiple payment modes used
        if (isStoreCreditPayment && isMultiplePaymentMethods(purchase, false) && !isFullRefund) {
            openTransferForm();
            return;
        }

        if (!paymentMethod) {
            // TODO: Rushi or Alexander to find a better way to check for falsy payment method
            console.log("ERROR - Payment method cannot be null");
            return;
        }

        const payload = getPayload(
            paymentMethod,
            purchase,
            selectedItem ?? item ?? null,
            values,
            shouldReduce,
            serviceFeeDelta,
            true,
        );
        const amountToRefund = roundNumber(purchase.currency, values.amount);
        await handleSubmitCommon(payload, amountToRefund);
    };

    const handleTransferStoreCredit = async (payload: any, amountToReturn: number) => {
        hideTransferForm();
        await handleSubmitCommon(payload, amountToReturn);
    };

    // Amount is the value based on the selected item and payment method including the presence of service fee
    // and who is paying the service fee
    let amount = getRefundAmount(purchase, selectedItem ?? item, paymentMethod);
    if (isInteracCardRefundFlow && selectedCard?.value) {
        // we cannot refund more than paid by the interac card value
        amount = Math.min(amount, selectedCard?.value ?? amount);
    }

    const [amountState, setAmountState] = useState<{ enteredAmount?: number; touched: boolean }>({
        enteredAmount: Math.min(amount, refundAmount ?? amount),
        touched: false,
    });

    if (isLoading) {
        return <Loader />;
    }

    const showSummaryTile = product && (shouldReduce || isExperienceItem);
    const showCapAmount = shouldReduce || !isStoreCredit;
    const excludedPaymentMethods = getExcludedPaymentMethods(purchase, item);
    const includePaymentMethods = getOnlinePaymentMethodsFromPurchase(purchase);

    const handlePaymentBreakdownUpdate = (updatedPurchase: Purchase | null, itemId?: ID) => {
        if (!updatedPurchase) {
            return;
        }

        if (getPartnerFeePaidByTraveler(purchase)) {
            // For flex level sellers the amount to return is different from the amount to reduce
            // This computes the difference between the original service fee and the new service fee, so we can decide
            // how much to add to the return payment ops for the return payment call
            const originalServiceFee = getPartnerFeePaidByTraveler(purchase) ?? 0;
            const newServiceFee = getPartnerFeePaidByTraveler(updatedPurchase);
            let newServiceFeeDelta = isUndefined(newServiceFee) ? 0 : originalServiceFee - newServiceFee;

            // If purchase is package or MIST,
            // There is no need to get service fee delta for the last item, we will have to pull the rest service fee left
            if (purchase.items.length > 1) {
                const selectedItemIndex = purchase.items.findIndex((item) => itemId && item.id === itemId);
                const isLastItem = selectedItemIndex >= 0 && selectedItemIndex === purchase.items.length - 1;
                if (isLastItem) {
                    newServiceFeeDelta = newServiceFee ?? 0;
                }
            }

            const isReturnPayment = refundOption === RefundOption.RETURN_PAYMENT && serviceFeeDelta === 0;

            // We need to check if the service fee delta is not 0, as otherwise we apply it twice for the cases like change Add-on Modal
            if (!isReturnPayment && !almostEquals(newServiceFeeDelta, serviceFeeDelta)) {
                setServiceFeeDelta(newServiceFeeDelta);
            }
        }
    };

    const handlePaymentMethodChange = (value: SellerPaymentMethod) => {
        setPaymentMethod(value);
        // This condition is required as gift payment method is not allowed for refund and PaymentBreakdown component
        // uses useDebounce hook which calls the API with previous values resulting in API error
        if (paymentMethod?.id === PaymentMethod.GIFT) {
            setIsPaymentMethodChanging(true);
            setTimeout(() => setIsPaymentMethodChanging(false), 600);
        }
    };

    // We need to keep amount value when user changes the payment method
    const handleAmountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^\d.]/g, "");
        const newValue = value ? Number.parseFloat(value) : undefined;
        setAmountState({ touched: true, enteredAmount: newValue });
    };

    if (!paymentMethod) {
        console.error("For refund payment method should always be there", paymentMethod);
    }

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnMount
            validateOnBlur
            initialErrors={
                restrictedPaymentMethods.has(paymentMethod?.id ?? "")
                    ? { paymentMethod: { id: "Payment Method is required" } }
                    : {}
            }
            initialValues={{
                amount: amountState.touched ? amountState.enteredAmount : Math.min(amount, refundAmount ?? amount), // Can't make an api call with amount greater than the refund item amount
                comment: commentState.touched ? commentState.comment : initialComment,
                paymentMethod: paymentMethod ?? initialPaymentMethod!,
                notify: isNotifyCustomer,
            }}
            validationSchema={getValidationSchema(purchase, selectedItem, paymentMethod, isInteracCardRefundFlow)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue, errors }) => {
                const breakdownEnabled =
                    !isSuccess && !errors.paymentMethod?.id && !isPaymentMethodChanging && !isUpdating;

                return (
                    <fieldset disabled={isUpdating}>
                        <Modal.Header>{headerText}</Modal.Header>
                        <Modal.Body className="flex flex-wrap">
                            <RefundWarnings
                                purchase={purchase}
                                paymentMethod={paymentMethod}
                                formAmount={values.amount ?? 0}
                                maxAmount={amount}
                                shouldReduce={shouldReduce}
                            />

                            {selectedItem && product && allowedItemsToReturnPayment.length > 1 && (
                                <PurchaseItemSelectWrapper
                                    purchase={purchase}
                                    items={allowedItemsToReturnPayment}
                                    item={selectedItem}
                                    product={product}
                                    onChange={handleSelectedItem}
                                />
                            )}

                            <div className="flex flex-wrap gap-4">
                                <div className="flex-1 space-y-4">
                                    {showSummaryTile && (
                                        <PurchaseSummaryTile purchase={purchase} itemId={selectedItem?.id} />
                                    )}

                                    {/* The max REDUCE amount is "purchase.amount - service fee" but max RETURN
                                        amount is the full amount */}
                                    <FormField prevalidate name="amount" label="Amount">
                                        <CurrencyInputField
                                            isRequired
                                            filterZero
                                            placeholder="Amount"
                                            onChange={handleAmountValueChange}
                                        />
                                        {showCapAmount && (
                                            <div
                                                data-testid="refund-cap-amount" // TODO: Change
                                                className="mt-1 w-full space-x-1 px-1 text-right text-sm text-gray-dark"
                                            >
                                                <span>up to</span>
                                                <Currency>{amount}</Currency>
                                            </div>
                                        )}
                                    </FormField>

                                    <PaymentMethodButtons
                                        isRefund
                                        value={paymentMethod!}
                                        error={errors.paymentMethod?.id}
                                        excludePaymentMethods={excludedPaymentMethods}
                                        includePaymentMethods={includePaymentMethods}
                                        isOtherSellerPurchase={isOtherSellerPurchase(purchase, seller.id)}
                                        onChange={(value) => {
                                            setFieldValue("paymentMethod", value);
                                            handlePaymentMethodChange(value);
                                        }}
                                    />

                                    <RefundToStoreCreditWarning purchase={purchase} paymentMethod={paymentMethod} />

                                    {isCreditCardPayment(paymentMethod) && (
                                        <SavedCards
                                            showAutomaticSplitRefund
                                            className="max-w-[350px]"
                                            purchase={purchase}
                                            onChange={setSelectedCard}
                                        />
                                    )}

                                    {isInteracCardRefundFlow && (
                                        <>
                                            <InteracPaymentMessage isEMVConnected={hasDevice} />
                                            <EmvRefundInput ref={emvRef} initialPurchase={purchase} />
                                        </>
                                    )}

                                    {isExperienceItem && refundOption && onChangeRefundOption && (
                                        <RefundOptionsDropdown
                                            label="Reason"
                                            purchase={purchase}
                                            currentOption={refundOption}
                                            onChange={onChangeRefundOption}
                                        />
                                    )}

                                    {!isInteracCardRefundFlow && (
                                        <FormField
                                            name="comment"
                                            label="Comment"
                                            prevalidate={amountState.touched && !!errors.comment}
                                        >
                                            <InputField
                                                isRequired
                                                data-testid="return-payment-comment"
                                                placeholder="Comment"
                                                onChange={(comment) => {
                                                    setCommentState({ touched: true, comment: comment });
                                                }}
                                            />
                                        </FormField>
                                    )}
                                </div>

                                <div className="flex-1">
                                    <div className="flex-1 rounded bg-gray-lighter p-8">
                                        <PaymentBreakdown
                                            isEnabled={breakdownEnabled}
                                            payload={getPayload(
                                                paymentMethod!,
                                                purchase,
                                                selectedItem,
                                                values,
                                                shouldReduce,
                                                serviceFeeDelta,
                                            )}
                                            purchase={purchase}
                                            item={selectedItem ?? undefined}
                                            isUpdating={setIsUpdatingPurchase}
                                            onUpdate={handlePaymentBreakdownUpdate}
                                        />
                                    </div>
                                </div>
                            </div>
                            <Printer
                                enablePaperCut
                                enableDrawerEject={enableDrawerEject}
                                shouldStart={enableDrawerEject}
                            />
                        </Modal.Body>

                        <Modal.Footer className="flex justify-end space-x-4">
                            {!isInteracCardRefundFlow && (
                                <Field name="notify">
                                    {() => (
                                        <NotifyCustomerCheckbox
                                            isChecked={isNotifyCustomer}
                                            onChange={async (e) => {
                                                setIsNotifyCustomer(e.target.checked);
                                                setFieldValue("notify", e.target.checked);
                                            }}
                                        />
                                    )}
                                </Field>
                            )}

                            <SecondaryButton onClick={onClose}>Skip</SecondaryButton>
                            <SubmitButton
                                type="button"
                                isLoading={isUpdating}
                                isSuccess={isSuccess}
                                disabled={isUpdatingPurchase || (isInteracCardRefundFlow && !hasDevice)}
                                onClick={handleSubmit}
                            >
                                Refund
                            </SubmitButton>
                        </Modal.Footer>

                        {Number(values?.amount) > 0 && (
                            <TransferToStoreCreditModal
                                isOpen={showTransferForm}
                                isLoading={isUpdating}
                                purchase={purchase}
                                item={selectedItem && "shortCode" in selectedItem ? selectedItem : null}
                                shouldReduce={shouldReduce}
                                amount={Number(values?.amount) + serviceFeeDelta}
                                serviceFeeDelta={serviceFeeDelta}
                                notify={values.notify}
                                onClose={hideTransferForm}
                                onSubmit={handleTransferStoreCredit}
                            />
                        )}
                    </fieldset>
                );
            }}
        </XolaFormik>
    );
};
